// src/pages/MyProfile.js
import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import { Edit, BookOpen, Award, User, LogOut } from "lucide-react";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import { UserContext } from "./context/UserContext";
import LogoutModal from "./components/LogoutModal";
import { useNavigate } from "react-router-dom";
import { db } from "./firebase/firebaseConfig";
import { doc, getDoc, collection, getDocs } from "firebase/firestore";

const MyProfile = () => {
  const { user, logout } = useContext(UserContext);
  console.log("user", user);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const navigate = useNavigate();

  // Fetch profile info
  useEffect(() => {
    const fetchProfile = async () => {
      if (!user?.id) {
        console.log("⛔ No user logged in or user.id missing");
        return;
      }

      try {
        const userRef = doc(db, "users", user.id); // 👈 use local user.id
        const snap = await getDoc(userRef);

        if (snap.exists()) {
          const data = snap.data();
          console.log("✅ Profile data:", data);
          setProfileData({ id: snap.id, ...data });
        } else {
          console.log("❌ No such user document in Firestore");
        }
      } catch (err) {
        console.error("🔥 Error fetching profile:", err);
      }
    };

    fetchProfile();
  }, [user?.id]);

  const renderButton = (status) => {
    if (status === "Completed")
      return <CourseButton variant="outline">Restart</CourseButton>;
    if (status === "In Progress")
      return <CourseButton variant="filled">Continue</CourseButton>;
    return <CourseButton variant="start">Start Course</CourseButton>;
  };

  if (!profileData) {
    return (
      <Container>
        <h2 style={{ color: "#fff", textAlign: "center" }}>
          Loading profile...
        </h2>
      </Container>
    );
  }

  return (
    <Container>
      {/* Header */}
      <Card>
        <ProfileSection>
          <Avatar>
            {profileData.profilePic ? (
              <img src={profileData.profilePic} alt="Profile" />
            ) : (
              <User />
            )}
          </Avatar>

          <Info>
            <h2>{profileData.name}</h2>
            <p className="email">{profileData.email}</p>
            {profileData.mobile && (
              <p className="mobile">📱 {profileData.mobile}</p>
            )}
            {profileData.profession && (
              <p className="profession">💼 {profileData.profession}</p>
            )}
            {/* <p className="bio">
              {profileData.bio ||
                "Full-stack Developer | Mobile & Web Specialist"}
            </p> */}
            <Skills>
              {(profileData.skills || []).map((skill, i) => (
                <Skill key={i}>{skill}</Skill>
              ))}
            </Skills>
          </Info>

          <EditButton onClick={() => navigate("/edit-profile")}>
            <Edit size={16} /> Edit Profile
          </EditButton>
          <LogoutButton onClick={() => setShowLogoutModal(true)}>
            <LogOut size={16} /> Logout
          </LogoutButton>
        </ProfileSection>
      </Card>

      {/* Stats */}
      <StatsGrid>
        <StatCard>
          <BookOpen size={28} color="#2563eb" />
          <h3>{enrolledCourses.length}</h3>
          <p>Enrolled Courses</p>
        </StatCard>
        <StatCard>
          <User size={28} color="#16a34a" />
          <h3>
            {enrolledCourses.filter((c) => c.status === "Completed").length}
          </h3>
          <p>Completed</p>
        </StatCard>
        <StatCard>
          <Award size={28} color="#9333ea" />
          <h3>{profileData.certificates || 0}</h3>
          <p>Certificates</p>
        </StatCard>
      </StatsGrid>

      {/* Courses */}
      <Card>
        <h2 className="section-title">Enrolled Courses</h2>
        <CoursesGrid>
          {enrolledCourses.length > 0 ? (
            enrolledCourses.map((course) => (
              <CourseCard key={course.id} status={course.status}>
                <ProgressWrapper>
                  <CircularProgressbar
                    value={course.progress || 0}
                    text={`${course.progress || 0}%`}
                    styles={buildStyles({
                      textColor: "#fff",
                      pathColor:
                        course.status === "Completed"
                          ? "#16a34a"
                          : course.status === "In Progress"
                          ? "#2563eb"
                          : "#555",
                      trailColor: "#333",
                    })}
                  />
                </ProgressWrapper>
                <CourseInfo>
                  <h3>{course.title}</h3>
                  <p>{course.status || "Not Started"}</p>
                  {renderButton(course.status || "Not Started")}
                </CourseInfo>
              </CourseCard>
            ))
          ) : (
            <p style={{ color: "#aaa" }}>No enrolled courses yet 🚀</p>
          )}
        </CoursesGrid>
      </Card>

      {/* Logout Modal */}
      <LogoutModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={() => {
          setShowLogoutModal(false);
          logout();
          navigate("/");
        }}
      />
    </Container>
  );
};

export default MyProfile;

// Styled Components (same as yours but responsive-ready)
const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Card = styled.div`
  background: #1a1a1a;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.3);
  color: white;

  .section-title {
    font-size: 20px;
    font-weight: bold;
    margin-bottom: 16px;
  }
`;

const Skills = styled.div`
  margin-top: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Skill = styled.span`
  padding: 6px 12px;
  font-size: 14px;
  border-radius: 20px;
  background: #2563eb22;
  color: #3b82f6;
`;

const EditButton = styled.button`
  padding: 8px 16px;
  background: transparent;
  border: 1px solid #444;
  border-radius: 6px;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
  &:hover {
    background: #333;
  }
`;

const LogoutButton = styled.button`
  padding: 8px 16px;
  background: transparent;
  border: 1px solid rgb(206, 14, 14);
  border-radius: 6px;
  color: rgb(206, 14, 14);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
  &:hover {
    background: rgb(246, 105, 105);
    color: white;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
`;

const StatCard = styled.div`
  background: #1a1a1a;
  padding: 20px;
  border-radius: 12px;
  text-align: center;
  color: white;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.2);

  h3 {
    font-size: 22px;
    margin-top: 8px;
  }

  p {
    color: #aaa;
  }
`;

const CoursesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 16px;
`;

const CourseCard = styled.div`
  background: #222;
  padding: 16px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: 0.2s;
  border-left: 5px solid
    ${(p) =>
      p.status === "Completed"
        ? "#16a34a"
        : p.status === "In Progress"
        ? "#2563eb"
        : "#555"};

  &:hover {
    background: #2a2a2a;
  }
`;

const ProgressWrapper = styled.div`
  width: 60px;
  height: 60px;
`;

const CourseInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;

  h3 {
    font-size: 16px;
    font-weight: bold;
  }

  p {
    margin-top: 4px;
    font-size: 14px;
    color: ${(p) =>
      p.status === "Completed"
        ? "#16a34a"
        : p.status === "In Progress"
        ? "#2563eb"
        : "#888"};
  }
`;

const CourseButton = styled.button`
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;

  ${(p) =>
    p.variant === "outline" &&
    `
    background: transparent;
    border: 1px solid #16a34a;
    color: #16a34a;
    &:hover { background: #16a34a22; }
  `}

  ${(p) =>
    p.variant === "filled" &&
    `
    background: #2563eb;
    color: white;
    &:hover { background: #1d4ed8; }
  `}

  ${(p) =>
    p.variant === "start" &&
    `
    background: #facc15;
    color: #111;
    &:hover { background: #eab308; }
  `}
`;

const ProfileSection = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
`;

// Avatar wrapper
const Avatar = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  border: 3px solid #00d4ff;
  background: #2a2a3d;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  svg {
    width: 60%;
    height: 60%;
    color: #888;
  }
`;

const Info = styled.div`
  flex: 1;
  min-width: 220px;

  h2 {
    font-size: 24px;
    font-weight: bold;
  }
  .email {
    color: #aaa;
  }
  .bio {
    margin-top: 8px;
    color: #ccc;
  }
  .mobile {
    margin-top: 6px;
    font-size: 14px;
    color: #bbb;
  }
  .profession {
    margin-top: 4px;
    font-size: 14px;
    font-weight: 500;
    color: #00d4ff;
  }
`;
